import * as Sentry from "@sentry/react";

import { ChartLayout } from "models/chart";
import { ChartSeries } from "models/model";

import {
  getYAxisUnit,
  scaleDataBasedOnYAxisUnits
} from "components/arps/utils/scaleDataBasedOnYAxisUnits";

import { MISSING_VALUE } from "../styles";

/** A whole number with thousands separators, or the panel's dash. */
export const formatProductionValue = (value: Maybe<number>): string =>
  value == null || !Number.isFinite(value)
    ? MISSING_VALUE
    : Math.round(value).toLocaleString("en-US");

export type ArrayProductKey = "oil" | "gas" | "water";

/** The unit a column's numbers are in, and how to get them there. */
export interface ArrayUnit {
  /** Multiply a value straight off the response by this to land in `label`. */
  readonly factor: number;
  /** The unit, written as a column header shows it. */
  readonly label: string;
  /** The axis is a rate per day, so a month's volume is it times the days. */
  readonly perDay: boolean;
  /** The same unit without the per-day part, which a volume column is in. */
  readonly baseLabel: string;
}

export interface ProductColumnUnits {
  readonly volume: string;
  readonly rate: string;
}

export type ProductionArrayUnits = Record<ArrayProductKey, ProductColumnUnits>;

/**
 * Used until a response lands, and for any product whose axis title carries no
 * unit at all. These are the design's literals, not an assumption about the
 * org: a response that names its unit always wins.
 */
export const DEFAULT_ARRAY_UNITS: ProductionArrayUnits = {
  oil: { volume: "bbl", rate: "bbl/d" },
  gas: { volume: "Mcf", rate: "Mcf/d" },
  water: { volume: "bbl", rate: "bbl/d" }
};

/**
 * The base unit each response unit reduces to. The array rounds every cell to a
 * whole number, so a month of 400 bbl on an axis labelled Mbbl would print as
 * 0 — values are scaled down to the base unit and the header follows them.
 *
 * A unit that is not on this list (a metric org's m3) is left exactly as the
 * response sent it, value and header together.
 */
const BASE_UNIT: Record<string, string> = {
  bbl: "bbl",
  mbbl: "bbl",
  mmbbl: "bbl",
  mcf: "Mcf",
  mmcf: "Mcf",
  bcf: "Mcf",
  scf: "Mcf"
};

/**
 * The axis a series is plotted against. A multi-phase response carries one axis
 * per product in `layout.yAxis2` and points each series at it by index — the
 * same resolution the chart tooltip uses
 * (`entities/charts/eva/tooltips/utils.ts:337-341`). `layout.yAxis` is the
 * single-axis shape and stands as the fallback.
 */
export function resolveYAxisTitle(
  series: Maybe<ChartSeries>,
  layout: Maybe<ChartLayout>
): string {
  const index = series?.yAxisIndex;
  const byIndex = typeof index === "number" ? layout?.yAxis2?.[index]?.title : null;
  return byIndex || series?.yAxisTitle || layout?.yAxis?.title || "";
}

/**
 * The unit and the factor for one column, both off the response's own axis
 * title. The request picks its unit per response - Mbbl/d for one well, bbl/d
 * for another - so a hardcoded factor is right only by luck and a hardcoded
 * header is a claim the numbers do not support.
 *
 * `scaleDataBasedOnYAxisUnits` converts a base value INTO the axis unit, so its
 * reciprocal takes the response's value back to base.
 */
export function deriveArrayUnit(
  title: Maybe<string>,
  fallback: ProductColumnUnits
): ArrayUnit {
  const { numerator, perDay, per, normalized } = getYAxisUnit(title ?? "");
  // The two columns take their own default: one fallback would print the
  // rate's unit over the volume column.
  if (!numerator)
    return { factor: 1, label: fallback.rate, perDay: false, baseLabel: fallback.volume };

  const scale = scaleDataBasedOnYAxisUnits(title ?? "");
  // A normalized number printed under a plain unit reads as the well's own
  // production, so the header carries the normalization the title names.
  const suffix = (per ? ` ${per}` : "") + (normalized ? ", normalized" : "");
  const base = BASE_UNIT[numerator.toLowerCase()] ?? numerator;
  return {
    factor: scale === 0 ? 1 : 1 / scale,
    label: (perDay ? base + "/d" : base) + suffix,
    // The volume column is this column's numbers times the month's days, so a
    // caller has to know whether they are per day before it multiplies them,
    // and the header it writes is this base rather than the per-day label.
    perDay,
    baseLabel: base + suffix
  };
}

const normalizeLabel = (label: Maybe<string>): string =>
  (label ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

const PRODUCT_ALIASES: Record<ArrayProductKey, readonly string[]> = {
  oil: ["oil", "oilcond"],
  gas: ["gas"],
  water: ["water"]
};

/**
 * Every product comes back as historical, forecast and backfit series. A
 * forecast series read as history produces rows near 1e-5, which look like a
 * dying well rather than an error, so the filter is not optional.
 *
 * More than one historical series for a product is not a shape the service is
 * expected to return. It is reported and the first is used: the table has to
 * pick a series either way, and failing the whole request presents an
 * unexpected shape as a failed fetch, which is a different and wrong thing to
 * tell the user.
 *
 * None at all is reported only when the response carried series of its own. A
 * response with none is a well that did not produce that product, and an empty
 * column is the right answer. Series that arrived under labels none of the
 * aliases match is the case worth knowing about: the column is empty for a
 * producing well, and nothing on screen tells the two apart.
 */
export function pickHistoricalSeries(
  series: Maybe<readonly ChartSeries[]>,
  product: ArrayProductKey
): Maybe<ChartSeries> {
  const aliases = PRODUCT_ALIASES[product];
  const responded = series ?? [];
  const matches = responded.filter((s) => {
    if (s?.isForecast) return false;
    const label = normalizeLabel(s?.label);
    if (label.includes("backfit") || label.includes("forecast")) return false;
    return aliases.includes(label);
  });

  const unmatched = matches.length === 0 && responded.length > 0;
  if (unmatched || matches.length > 1) {
    Sentry.withScope((scope) => {
      scope.setContext("well_card_production_array", {
        product,
        matchCount: matches.length,
        respondedCount: responded.length,
        labels: (unmatched ? responded : matches).map((s) => s?.label),
        action: "pickHistoricalSeries"
      });
      scope.setLevel("warning");
      Sentry.captureMessage(
        unmatched
          ? `Production array: the response carried series but none matched ${product}. The column stays empty.`
          : `Production array: expected one historical ${product} series, got ${matches.length}. Using the first.`
      );
    });
  }
  return matches[0] ?? null;
}

/**
 * The month's volume behind a calendar-day rate.
 *
 * The caller holds the invariant: the request asks for a calendar-day rate, so
 * the rate is the month's volume over the month's calendar days and
 * multiplying them back is exact rather than an estimate. February takes 28 or
 * 29 from the calendar.
 */
export function volumeFromCalendarDayRate(
  rate: Maybe<number>,
  month: Maybe<CalendarMonth>
): Maybe<number> {
  if (rate === null || rate === undefined || month === null || month === undefined) {
    return null;
  }
  const daysInMonth = new Date(Date.UTC(month.year, month.month + 1, 0)).getUTCDate();
  return rate * daysInMonth;
}

export const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec"
] as const;

export interface CalendarMonth {
  readonly year: number;
  /** 0-based. */
  readonly month: number;
  readonly label: string;
}

/**
 * The calendar month an x value from a date-axis series falls in.
 *
 * The response's own dates are what the array is labelled with, so a well whose
 * reported first-production date disagrees with its production records cannot
 * push the rows into a year that never happened.
 *
 * The axis is ECharts' `time` axis, which takes any of the shapes the service
 * writes: a date string with or without a time, milliseconds, and seconds.
 */
export function timeAxisValueToMs(value: Maybe<number | string>): Maybe<number> {
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    // A month index is a small number, and read as a date it lands in 1970 -
    // a whole table inside one year group, with every volume off by the days
    // of January. Below this the value is not a date in either unit.
    if (Math.abs(value) < 1e6) return null;
    // Seconds, because that is what the service reads its own numeric x back
    // as (`RateDateChart.cs`, `DateTimeOffset.FromUnixTimeSeconds`). Picking
    // whichever unit lands in a plausible year instead reads a 1970
    // millisecond value as seconds and dates the row to 2054.
    return nullIfOutsideProductionYears(value * 1000);
  }

  if (typeof value !== "string" || value.trim() === "") return null;
  // A date-time with no offset is local time to Date.parse, and local time a
  // few hours east of UTC is the month before. The service writes its dates
  // without one, so the zone is supplied here.
  const text = value.trim();
  // A date with no time is already read as UTC; one with a time is not.
  const carriesZone = /(z|[+-]\d{2}:?\d{2})$/i.test(text);
  const needsZone = /\d{2}:\d{2}/.test(text) && !carriesZone;
  const parsed = Date.parse(needsZone ? text + "Z" : text);
  return Number.isNaN(parsed) ? null : nullIfOutsideProductionYears(parsed);
}

/** Any year a well could have produced in, so a misread unit is caught. */
function withinProductionYears(ms: number): boolean {
  const year = new Date(ms).getUTCFullYear();
  return year >= 1900 && year <= 2100;
}

function nullIfOutsideProductionYears(ms: number): Maybe<number> {
  return withinProductionYears(ms) ? ms : null;
}

export function calendarFromSeriesX(value: Maybe<number | string>): Maybe<CalendarMonth> {
  const ms = timeAxisValueToMs(value);
  if (ms === null) return null;

  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return null;

  // UTC throughout: a month boundary read in a western local zone lands the
  // row in the month before the one the service reported.
  const month = date.getUTCMonth();
  return { year: date.getUTCFullYear(), month, label: MONTH_LABELS[month] };
}
